import { NoSsr, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { getYear } from 'date-fns'
import type { ReactNode } from 'react'
import { Link } from 'settle-ui/components/Link/Link'
import { RouterPage } from 'lib/navigation/consts'
import { useWelcomeVerifiedUser } from 'lib/hooks/useWelcomeVerifiedUser/useWelcomeVerifiedUser'
import { sessionStorageKeys } from 'lib/consts/browserStorage'
import { marketingLinks } from 'lib/consts/externalLinks'
import { getSSItem } from 'lib/helpers/getSSItem/getSSItem'
import { type ScheduleDemoLinkProps, ScheduleDemoLink } from './components/ScheduleDemoLink/ScheduleDemoLink'
import * as $ from './OnboardingLayout.styled'
import {
  OnboardingTopSection,
  type OnboardingTopSectionProps,
} from './components/OnboardingTopSection/OnboardingTopSection'
import { useOnboardingLayoutContext } from './hooks/useOnboardingLayoutContext/useOnboardingLayoutContext'
import { BusinessTypeEnum } from 'gql/types'

export interface OnboardingLayoutProps
  extends Partial<Pick<OnboardingTopSectionProps, 'businessType' | 'progress' | 'withProgressBar'>> {
  demoUrl?: ScheduleDemoLinkProps['url'] | null
  slots?: {
    secondary?: ReactNode
  }
  slotProps?: {
    content?: Pick<React.ComponentProps<typeof $.Content>, 'sx'>
    paper?: Pick<React.ComponentProps<typeof $.Paper>, 'sx'>
    // `main.sx` padding is overridden by the higher-specificity `* + &` rule when a `secondary` slot
    // is also present (the styled Main re-pads itself next to a sibling) — don't rely on it for that case.
    main?: Pick<React.ComponentProps<typeof $.Main>, 'sx'>
    footer?: Pick<React.ComponentProps<typeof $.Footer>, 'sx'>
  }
}

export const OnboardingLayout: Settle.FC<OnboardingLayoutProps> = ({
  slots,
  slotProps,
  demoUrl,
  children,
  withProgressBar = true,
  businessType = BusinessTypeEnum.PAYER,
  progress,
  ...props
}) => {
  useWelcomeVerifiedUser()
  const { t } = useTranslation()
  const { flowType } = useOnboardingLayoutContext()
  const { secondary } = slots ?? {}

  const isDemoScheduled = Boolean(getSSItem(sessionStorageKeys.isDemoScheduled))
  const renderedDemoLink = isDemoScheduled ? null : demoUrl

  return (
    <$.Root {...props}>
      <$.Content {...slotProps?.content}>
        <OnboardingTopSection
          visibility="desktop"
          businessType={businessType}
          withProgressBar={!flowType && withProgressBar}
          progress={progress}
        />
        <$.Paper {...slotProps?.paper}>
          {secondary && <$.Secondary>{secondary}</$.Secondary>}
          <$.Main {...slotProps?.main}>
            <OnboardingTopSection
              visibility="mobile"
              businessType={businessType}
              withProgressBar={withProgressBar}
              progress={progress}
            />
            {children}
          </$.Main>
        </$.Paper>
        <$.Footer {...slotProps?.footer}>
          <Typography color="inherit" variant="body2">
            Settle © <NoSsr>{getYear(new Date())}</NoSsr>
          </Typography>
          <Link href={RouterPage.termsOfService} color="inherit" variant="body2">
            {t('termsOfService')}
          </Link>
          <Link href={marketingLinks.privacyPolicy} color="inherit" variant="body2">
            {t('privacy')}
          </Link>
        </$.Footer>
      </$.Content>
      {renderedDemoLink && <ScheduleDemoLink url={renderedDemoLink} />}
    </$.Root>
  )
}
